import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import style from "./PreviewV2ToolBar.module.scss";
import { useSignal } from "@preact/signals-react";
import { ToolBarConfigValue, ToolBarConfigKeyType, TOOLBAR_CONFIG } from "./PreviewV2ToolBarUtil";
import { Popover } from "antd";
import { useHomeUsePluginContainer } from "@custom-hooks/HomeUsePluginRoot";
import { useAISaasPreviewItem } from "../AISaas/hooks/useAISaasPreviewItem";
import { GROUPID_REG } from "@views/thinking-layout-editor/constants";
import { logEvent } from "@uikit/service/amplitude";

const initState: ToolBarConfigValue = {
  fontStyleKey: "",
  componentsConfig: [
    {
      component: null,
      tips: "",
    },
  ],
};
// main view
export function PreviewV2ToolBar(props: any) {
  const { saasAndThinkState } = useHomeUsePluginContainer();

  const { saasUIData, setSaasUIData } = saasAndThinkState;

  const currentConfig = useSignal<ToolBarConfigValue>(initState);

  const {
    previewHoverData: { id, page = "", type, show, container, edit, remove, editPlaceholder, selectTips },
    setPreviewHoverData,
  } = useAISaasPreviewItem();

  const isGroup = GROUPID_REG.test(id || "");
  const currentOutput = useMemo(() => {
    return (
      saasUIData.output.find((x: any) => {
        const currentId = isGroup ? x.groupId : x.results[0].previewId;
        return currentId === id;
      }) || {}
    );
  }, [saasUIData.output, id]);

  const onClickEdit = (tips: string | string[]) => {
    edit && edit(tips);
  };

  //  删除
  const onClickRemove = () => {
    remove && remove();
  };
  //  编辑placeholder
  const onClickEditPlaceholder = (tips: string | string[]) => {
    editPlaceholder && editPlaceholder(tips);
  };

  const clickOutputStateCp = (key: string) => {
    const newOutputs = saasUIData.output.map((o: any) => {
      const currentId = isGroup ? o.groupId : o.results[0].previewId;
      if (currentId === id) {
        const isShowCaption = !o.results[0]?.[key] ?? false;

        logEvent("publish_preview_caption", { value: isShowCaption });
        return {
          ...o,
          results: [
            {
              ...o.results[0],
              [key]: !o.results[0]?.[key],
            },
          ],
        };
      }
      return o;
    });
    setSaasUIData((prevData: any) => {
      return {
        ...prevData,
        output: newOutputs,
      };
    });

    /// BSF-4552: 同时更新 thoughts 数据
    // bsf-4912 use setSassUIData
    setSaasUIData(prev => ({ ...prev, output: newOutputs }));
  };

  const clickOutputCssStyleCp = (key: string) => {
    const keyObj: {
      [key: string]: any;
    } = {
      isBold: {
        styleKey: "fontWeight",
        style: {
          fontWeight: "bold",
        },
      },
      isItalic: {
        styleKey: "fontStyle",
        style: {
          fontStyle: "italic",
        },
      },
      isUnderLine: {
        styleKey: "textDecoration",
        style: {
          textDecoration: "underline",
        },
      },
      isBeautify: {
        styleKey: "background",
        defaultStyle: saasUIData.theme === "light" ? "#fff" : "rgba(255, 255, 255, 0.1)",
        style: {
          background: "transparent",
        },
      },
    };

    const newOutputs = saasUIData.output.map((i: any) => {
      const currentId = isGroup ? i.groupId : i.results[0].previewId;
      if (currentId === id) {
        // fontLabel是通过fontFamily来设定是否加粗
        let boldReset = {};
        const needHandleBold = key === "isBold" && i.results[0]?.fontStyleKey === "fontLabel";
        // 已设置则重置为空
        if (i.results[0]?.[key]) {
          if (needHandleBold) {
            boldReset = { fontFamily: "ak-regular" };
          }
          return {
            ...i,
            results: [
              {
                ...i.results[0],
                [key]: false,
                nodeFontStyle: {
                  ...(i.results[0].nodeFontStyle || {}),
                  [keyObj[key].styleKey]: keyObj[key]?.defaultStyle || "unset",
                  ...boldReset,
                },
              },
            ],
          };
        }

        // 设置值
        if (needHandleBold) {
          boldReset = {
            fontFamily: "ak-regular",
            fontWeight: 800,
          };
        }
        return {
          ...i,
          results: [
            {
              ...i.results[0],
              [key]: true,
              nodeFontStyle: {
                ...(i.results[0].nodeFontStyle || {}),
                ...keyObj[key].style,
                ...boldReset,
              },
            },
          ],
        };
      }
      return i;
    });

    setSaasUIData((prevData: any) => {
      return {
        ...prevData,
        output: newOutputs,
      };
    });

    /// BSF-4552: 同时更新 thoughts 数据
    // bsf-4912 use setSassUIData
    setSaasUIData(prev => ({ ...prev, output: newOutputs }));
  };

  const getCurrentTips = (param: { dataStateKey?: string; tips: string | string[] }) => {
    const { tips, dataStateKey } = param;
    if (Array.isArray(tips) === false) return tips;
    if (dataStateKey) {
      return (currentOutput as any).results?.[0]?.[dataStateKey] ? tips[1] : tips[0];
    }
    return tips;
  };

  useEffect(() => {
    currentConfig.value = TOOLBAR_CONFIG[type as ToolBarConfigKeyType] || initState;
  }, [type]);

  return (
    <>
      {" "}
      {show && container
        ? createPortal(
            <div
              data-testid="PreviewV2ToolBar"
              className={style["toolbar-main"]}
              onMouseLeave={() => {
                setPreviewHoverData({
                  show: false,
                  container: null,
                  type: "",
                });
              }}
              style={page === "chat" ? {} : { marginTop: 5 }}
            >
              <div id="preview-toollbar-box" className={style["toolbar-box"]}>
                {currentConfig.value.componentsConfig.map((c, index) => {
                  return (
                    c.component && (
                      <Popover
                        autoAdjustOverflow={false}
                        overlayClassName={style.textPopover}
                        getPopupContainer={(e: any) => {
                          return e;
                        }}
                        content={<div className={style.tooltipContainer}>{getCurrentTips(c)}</div>}
                        key={index}
                      >
                        <div className={style["popover-box"]}>
                          {React.createElement(c.component, {
                            ...props,
                            id,
                            isGroup,
                            fontStyleKey: currentConfig.value.fontStyleKey,
                            currentOutput,
                            saasUIData,
                            setSaasUIData,
                            onClickEdit: () => {
                              onClickEdit(getCurrentTips(c));
                            },
                            onClickRemove,
                            onClickEditPlaceholder: () => {
                              onClickEditPlaceholder(getCurrentTips(c));
                            },
                            clickOutputStateCp,
                            clickOutputCssStyleCp,
                            highlightBackground:
                              selectTips?.id === id &&
                              selectTips?.tips === getCurrentTips(c) &&
                              selectTips?.type === type,
                          })}
                        </div>
                      </Popover>
                    )
                  );
                })}
              </div>
            </div>,
            container
          )
        : null}
    </>
  );
}
